import { Builder } from '@builder.io/react'
import React, { useEffect, useState } from 'react'

import './index.css'
import { HeadingClass, HeadingType } from 'enums'
import LinkButton, { LinkButtonProp, LinkButtonRegisterProps } from '../common/button'
import CustomHeading, { headingClassPropObj, headingTypePropObj } from '../common/heading'

interface Props {
  backgroundImage: any
  mobileBgImage: any
  image: any
  imageAltText: string
  imageEnable: boolean
  buttonEnabled: boolean
  buttonProp: LinkButtonProp
  mediaBackdropColor: string
  headingType: HeadingType
  headingClass: HeadingClass
  headingText: string
  headingTextColor: string
  paragraphColor: string
  paragraph: string
  paragraphRichText: string
}

const HeroFour = ({
  backgroundImage = '',
  mobileBgImage,
  image,
  imageAltText = '',
  imageEnable,
  buttonEnabled,
  buttonProp,
  mediaBackdropColor,
  headingText,
  headingTextColor,
  headingType,
  headingClass,
  paragraphColor,
  paragraph,
  paragraphRichText
}: Props) => {
  const renderImage = () => {
    return (
      <img
        className="height-specifier relative w-full object-cover rounded-3xl"
        style={{ zIndex: 2 }}
        src={
          image ||
          'https://images.unsplash.com/photo-1556740758-90de374c12ad?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
        }
        alt={imageAltText}
      />
    )
  }

  const renderCTAButton = () => {
    if (buttonEnabled) {
      return (
        <LinkButton
          buttonColor={buttonProp?.buttonColor}
          buttonText={buttonProp?.buttonText}
          buttonTextColor={buttonProp?.buttonTextColor}
          buttonUrl={buttonProp?.buttonUrl}
          loggedInButtonText={buttonProp?.loggedInButtonText}
          isLink={buttonProp.isLink}
          classList={
            'mt-8 w-8/12 sm:w-7/12 mx-auto sm:mx-0 flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-indigo-700 bg-indigo-100 hover:bg-indigo-200 md:py-4 md:text-lg md:px-10'
          }
        />
      )
    }

    return null
  }

  const [isMobile, setIsMobile] = useState(false)

  const handleWindowSizeChange = () => {
    if (window.innerWidth <= 640) {
      setIsMobile(true)
    } else {
      setIsMobile(false)
    }
  }

  useEffect(() => {
    handleWindowSizeChange()
    window.addEventListener('resize', handleWindowSizeChange)

    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])

  return (
    <div
      className="relative w-full bg-white bg-cover bg-bottom bg-no-repeat"
      style={{ backgroundImage: `url(${isMobile ? mobileBgImage : backgroundImage})` }}
    >
      {/* <div className="bg-svg-wrapper">
        <svg width="100%" height="100%" viewBox="0 0 1440 1015" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M25.4095 146.462C-265.211 117.041 -441.955 246.878 -494 315.475V1015H1639V146.462C1521.43 85.6902 1204.94 -35.0702 879.48 -31.9403C472.661 -28.028 388.685 183.238 25.4095 146.462Z" fill="#F6E9DB"/>
        </svg>
      </div> */}

      <main className="container mx-auto py-5 md:flex lg:-mt-12 lg:pt-12">
        <div className="md:w-7/12">
          <div className="mx-auto flex h-full w-11/12 flex-col justify-center sm:w-3/4 md:w-full">
            <CustomHeading
              classList="text-white tracking-tight"
              headingClass={headingClass}
              headingType={headingType}
              color={headingTextColor}
            >
              {headingText}
            </CustomHeading>
            {!paragraphRichText && (
              <p className="mt-6 max-w-lg text-xl text-indigo-200 sm:max-w-3xl" style={{ color: paragraphColor }}>
                {paragraph}
              </p>
            )}
            {paragraphRichText && (
              <div
                className="mt-6 max-w-lg text-xl text-indigo-200 sm:max-w-3xl"
                style={{ color: paragraphColor }}
                dangerouslySetInnerHTML={{ __html: paragraphRichText }}
              />
            )}
            {renderCTAButton()}
          </div>
        </div>
        {imageEnable && (
          <div className="py-8 md:w-5/12">
            <div className="height-specifier relative mx-auto h-full w-10/12 sm:w-3/4 md:ml-5">
              <div
                className="video-backdrop-square-wrapper absolute h-full w-full"
                style={{ backgroundColor: mediaBackdropColor, zIndex: 1 }}
              />
              {renderImage()}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

Builder.registerComponent(HeroFour, {
  name: 'HeroFour',
  inputs: [
    {
      name: 'backgroundImage',
      type: 'file',
      defaultValue: null
    },
    {
      name: 'imageAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'mobileBgImage',
      type: 'file',
      defaultValue: null
    },
    {
      name: 'image',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'imageEnable',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'mediaBackdropColor',
      type: 'color',
      defaultValue: 'cyan'
    },
    {
      name: 'buttonEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      ...LinkButtonRegisterProps,
      name: 'buttonProp'
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Boost your productivity. Start using workflow today.'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'paragraphColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'paragraph',
      type: 'text',
      defaultValue: 'The main paragraph'
    },
    {
      name: 'paragraphRichText',
      type: 'richText',
      defaultValue: ''
    }
  ]
})

export default HeroFour
